import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SmartDetectionTypes } from '@shared-features/enums/smart-detection-types.enum';
import { DriversByDetectionType } from '@shared-features/enums/drivers-by-detection-type.enum';
import { UntypedFormControl } from '@angular/forms';
import { takeUntil, tap } from 'rxjs/operators';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { SharedConstants } from '@shared/model/shared-constants';
import debounce from 'lodash/debounce';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { LocationModel } from '@features/live-tracking/models/location.model';
import { Tracking } from '@features/live-tracking/models/tracking-driver';
import { ShipmentsApiService } from '@features/shipments/services/shipments-api.service';
import { DetectionStatus } from '@shared-features/enums/detection-status.enum';

@Component({
  selector: 'app-shipment-detection-tab',
  templateUrl: './shipment-detection-tab.component.html',
  styleUrls: ['./shipment-detection-tab.component.scss'],
})
export class ShipmentDetectionTabComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() pickupLocation: LocationModel;
  @Input() customerId: number;
  @Output() selectDriver = new EventEmitter();
  @Input() status: DetectionStatus;
  highlightedDriverId: number;
  drivers: Tracking[] = [];
  drivers$: Observable<Tracking[]>;
  selectedDriver = new UntypedFormControl();
  detectionTypes = SmartDetectionTypes;
  detectionType$: BehaviorSubject<SmartDetectionTypes> = new BehaviorSubject(
    SmartDetectionTypes.List
  );
  distanceRange = 100;
  zoom = 10;
  sharedConstants = SharedConstants;
  driversFreelanceChanged = true;
  driversFleetChanged = true;
  fleetType: DriversByDetectionType[] = [
    DriversByDetectionType.Specific,
    DriversByDetectionType.Freelance,
  ];
  googleMapLoaded = false;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private locationsApiService: LocationsApiService,
    private shipmentsApiService: ShipmentsApiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.locationsApiService.loadGoogleMapsApi().then(() => {
      this.googleMapLoaded = true;
    });
    this.setZoomLevel(this.distanceRange);
    this.detectDriversLocations();
    this.handleSelectedDriver();
  }

  ngOnChanges(): void {
    debounce(() => this.changeDetector.markForCheck(), 300);
  }

  private detectDriversLocations(): void {
    this.load(
      this.shipmentsApiService.detectDriversLocations({
        latitude: this.pickupLocation?.latitude,
        longitude: this.pickupLocation?.longitude,
        maximumDistanceInMeter: this.distanceRange * 1000,
        driverStatus: this.status,
        customerId: this.customerId,
        'fleetTypes[]': this.fleetType,
      }),
      {
        isLoadingTransparent: true,
        emptyContentCheck: (data) =>
          Array.isArray(data) &&
          !data.length &&
          this.detectionType$.value === this.detectionTypes.List,
      }
    ).subscribe((drivers) => {
      this.drivers = drivers;
      debounce(() => this.changeDetector.markForCheck(), 300);
    });
  }
  onDriversFreelanceChanged(event: MatCheckboxChange) {
    this.driversFreelanceChanged = event.checked;
    if (event.checked) {
      this.fleetType.push(DriversByDetectionType.Freelance);
    } else {
      this.fleetType = this.fleetType.filter((item) => item != DriversByDetectionType.Freelance);
    }
    this.detectDriversLocations();
  }

  onDriversFleetChanged(event: MatCheckboxChange) {
    this.driversFleetChanged = event.checked;
    if (event.checked) {
      this.fleetType.push(DriversByDetectionType.Specific);
    } else {
      this.fleetType = this.fleetType.filter((item) => item != DriversByDetectionType.Specific);
    }
    this.detectDriversLocations();
  }

  onRangeChange(range: number) {
    this.distanceRange = range;
    this.setZoomLevel(range);
    this.detectDriversLocations();
  }

  setZoomLevel(range: number) {
    const totalZoom = 5;
    const maxZoom = 10;
    const maxRange = 1000;
    const ratio = range / maxRange;
    this.zoom = maxZoom - totalZoom * ratio;
  }

  formatDistanceRange(value: number): string {
    return value ? `${value} Km` : '0 Km';
  }

  driverOnMap(id: number) {
    this.detectionType$.next(this.detectionTypes.Map);
    this.highlightedDriverId = id;
  }

  private handleSelectedDriver(): void {
    this.selectedDriver.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.selectDriver.emit(this.selectedDriver.value))
      )
      .subscribe();
  }
}
